import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Package,
  ShoppingCart,
  Tag,
  Inbox,
  Truck,
  BellRing,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { STATUS_LABEL } from "@/lib/constants";

const KPIS = [
  { key: "products", label: "Products", icon: Package, to: "/admin/products" },
  { key: "new_orders", label: "New Orders", icon: ShoppingCart, to: "/admin/orders", accent: true },
  { key: "app_subscribers", label: "App Subscribers", icon: BellRing, to: "/admin/app-notifications" },
  { key: "categories", label: "Categories", icon: Tag, to: "/admin/categories" },
  { key: "new_inquiries", label: "New Inquiries", icon: Inbox, to: "/admin/inquiries", accent: true },
  { key: "active_shipments", label: "Active Shipments", icon: Truck, to: "/admin/tracking" },
];

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
  });

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <span className="badge-purple">
            <Sparkles className="h-3.5 w-3.5" /> Trade Desk Overview
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Dashboard
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Quick snapshot of catalog, inquiries, and active shipments.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-5">
        {KPIS.map((k) => (
          <Link
            to={k.to}
            key={k.key}
            className="rsi-card p-6"
            data-testid={`kpi-${k.key}`}
          >
            <div className="flex items-center justify-between">
              <div
                className={`h-11 w-11 rounded-lg grid place-items-center ${
                  k.accent
                    ? "bg-[var(--rsi-purple)] text-white"
                    : "bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)]"
                }`}
              >
                <k.icon className="h-5 w-5" />
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </div>
            <div className="mt-5 font-display text-4xl font-black tracking-tighter text-[var(--rsi-navy)]">
              {isLoading ? "—" : data?.totals?.[k.key] ?? 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">{k.label}</div>
          </Link>
        ))}
      </div>

      {/* Recent */}
      <div className="grid xl:grid-cols-3 gap-6 mt-8">
        <div className="rsi-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
              Recent Orders
            </h3>
            <Link to="/admin/orders" className="text-xs font-semibold text-[var(--rsi-purple)]">
              View all
            </Link>
          </div>
          {data?.recent_orders?.length ? (
            <div className="divide-y divide-gray-100">
              {data.recent_orders.map((order) => (
                <div key={order.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono-plex font-semibold text-[var(--rsi-navy)] text-sm">
                      {order.order_number}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {order.customer_name} · {order.country || "—"}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {order.total_line_items} lines · {order.total_units} units
                    </div>
                  </div>
                  <span className="badge-purple capitalize">{String(order.status || "new").replaceAll("_", " ")}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-8 text-center">No app orders yet.</div>
          )}
        </div>

        <div className="rsi-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
              Recent Inquiries
            </h3>
            <Link to="/admin/inquiries" className="text-xs font-semibold text-[var(--rsi-purple)]">
              View all
            </Link>
          </div>
          {data?.recent_inquiries?.length ? (
            <div className="divide-y divide-gray-100">
              {data.recent_inquiries.map((i) => (
                <div key={i.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[var(--rsi-navy)] truncate">
                      {i.company_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {i.contact_name} · {i.email}
                    </div>
                    {i.product_name && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        For: <span className="text-[var(--rsi-purple)]">{i.product_name}</span>
                      </div>
                    )}
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      i.status === "new"
                        ? "bg-[var(--rsi-purple)] text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {i.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-8 text-center">No inquiries yet.</div>
          )}
        </div>

        <div className="rsi-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
              Recent Shipments
            </h3>
            <Link to="/admin/tracking" className="text-xs font-semibold text-[var(--rsi-purple)]">
              View all
            </Link>
          </div>
          {data?.recent_shipments?.length ? (
            <div className="divide-y divide-gray-100">
              {data.recent_shipments.map((s) => (
                <div key={s.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono-plex font-semibold text-[var(--rsi-navy)] text-sm">
                      {s.tracking_id}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {s.customer_name} → {s.destination || "—"}
                    </div>
                  </div>
                  <span className="badge-purple">{STATUS_LABEL[s.current_status]}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-8 text-center">No shipments yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
